import { Button } from "components/DesignSystem/Button/Button";
import ChatCard from "components/DesignSystem/ChatCard/ChatCard";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { CollapsibleDivider } from "components/icons/CollapsibleDivider";
import { GOOD_FEEDBACK, IMPROVE_FEEDBACK } from "constants/customerFeedback";
import { Form, Formik } from "formik";
import { useToast } from "hooks/useToast";
import { useEffect, useState } from "react";
import {
  CustomerFeedback,
  CustomerFeedbackSectionKey,
} from "types/Models/reviewDocumentAndBalancingPayment";
import { BackendError } from "types/utils/error";
import { CustomerFeedbackButton } from "./CustomerFeedbackButton";
import { CustomerFeedbackCardHeader } from "./CustomerFeedbackCardHeader";
import { FeedbackSectionInput } from "./FeedbackSectionInput";
import { RecordedCustomerFeedbackCloseView } from "./RecordedCustomerFeedbackCloseView";
import { StarRating } from "./StarRating";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useSubmitCustomerFeedbackMutation } from "store/apis/task";
import { capitalize } from "utils/capitalize";

type CustomerFeedbackCardProps = {
  feedback: CustomerFeedback;
  header: string;
  size?: "regular" | "block";
  groupId: string;
};

type FeedbackFormValues = {
  rating: number;
  goodFeedback: string[];
  improveFeedback: string[];
  additionalFeedback: string;
};

const SECTION_KEY_MAP = {
  [GOOD_FEEDBACK]: "goodFeedback",
  [IMPROVE_FEEDBACK]: "improveFeedback",
} as const;

export const CustomerFeedbackCard = ({
  feedback,
  header,
  size = "regular",
  groupId,
}: CustomerFeedbackCardProps) => {
  const [showAdditionalFeedback, setShowAdditionalFeedback] = useState(
    Boolean(feedback.additional_feedback)
  );
  const [isExpanded, setExpanded] = useState(false);
  const { alertToast, successToast } = useToast();
  const { isAdmin } = useRoleBasedView();
  const [submitFeedback, { isLoading }] = useSubmitCustomerFeedbackMutation();

  useEffect(() => {
    setShowAdditionalFeedback(Boolean(feedback.additional_feedback));
  }, [feedback?.additional_feedback]);

  const isSubmitted = feedback.submitted_by !== null;

  const getSelectedByKey = (key: CustomerFeedbackSectionKey) =>
    feedback.section.find((s) => s.key === key)?.selected || [];

  const initialValues: FeedbackFormValues = {
    rating: feedback.rating,
    goodFeedback: getSelectedByKey(GOOD_FEEDBACK),
    improveFeedback: getSelectedByKey(IMPROVE_FEEDBACK),
    additionalFeedback: feedback.additional_feedback || "",
  };

  const handleSubmit = async (values: FeedbackFormValues) => {
    try {
      const payload = {
        rating: values.rating,
        additional_feedback: values.additionalFeedback,
        good_feedback: values.goodFeedback,
        improve_feedback: values.improveFeedback,
      };
      await submitFeedback({
        feedbackId: feedback.feedback_id,
        groupId,
        payload,
      }).unwrap();
      successToast({
        message: `${capitalize(
          feedback.feedback_for
        )} feedback has been submitted`,
      });
    } catch (error: any) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  if (isSubmitted && !isExpanded) {
    return (
      <RecordedCustomerFeedbackCloseView
        feedback={feedback}
        header={header}
        size={size}
        setExpanded={setExpanded}
      />
    );
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values, setFieldValue }) => {
        const hasRating = values.rating > 0;
        const showSections = hasRating;

        const handleToggleChoice = (
          key: CustomerFeedbackSectionKey,
          choice: string
        ) => {
          const formKey = SECTION_KEY_MAP[key];
          const currentSelected = values[formKey];
          const isSelected = currentSelected.includes(choice);

          const newSelected = isSelected
            ? currentSelected.filter((c: string) => c !== choice)
            : [...currentSelected, choice];

          setFieldValue(formKey, newSelected);
        };

        return (
          <Form>
            <ChatCard.Root size={size}>
              <ChatCard.Header>
                <CustomerFeedbackCardHeader
                  isSubmitted={isSubmitted}
                  header={header}
                />
              </ChatCard.Header>
              <ChatCard.Body>
                <div className="t-flex t-flex-col t-gap-4 t-mt-2">
                  <div className="t-flex t-flex-col t-gap-1.5 t-items-center">
                    <div className="t-text-subtext-sm t-text-text-60">
                      How did we do on {feedback.name}?
                    </div>
                    <StarRating
                      rating={values.rating}
                      onChange={(rating) => setFieldValue("rating", rating)}
                      readonly={isSubmitted || isAdmin}
                    />
                  </div>

                  {showSections &&
                    feedback.section.map((section) => (
                      <>
                        <CollapsibleDivider className="t-text-neutral-10 t-mx-auto" />
                        <FeedbackSectionInput
                          key={section.key}
                          section={section}
                          selectedChoices={values[SECTION_KEY_MAP[section.key]]}
                          onToggleChoice={(choice) =>
                            handleToggleChoice(section.key, choice)
                          }
                          readonly={isSubmitted}
                        />
                      </>
                    ))}

                  {showSections && !showAdditionalFeedback && !isSubmitted && (
                    <Button
                      type="button"
                      customType="link"
                      size="small"
                      onClick={() => setShowAdditionalFeedback(true)}
                      className="t-self-center"
                    >
                      Add additional feedback
                    </Button>
                  )}

                  <div className="t-flex t-flex-col t-gap-2.5">
                    <div className="t-flex t-flex-col t-gap-1">
                      {showAdditionalFeedback && (
                        <TextArea
                          placeholder="Write your thoughts here"
                          rows={3}
                          disabled={isSubmitted}
                          name="additionalFeedback"
                        />
                      )}

                      {isSubmitted && feedback.submitted_by && (
                        <div className="t-text-body-sm t-italic t-mx-auto t-text-text-30">
                          Submitted by {feedback.submitted_by}
                        </div>
                      )}
                    </div>

                    <CustomerFeedbackButton
                      isSubmitted={isSubmitted}
                      setExpanded={setExpanded}
                      hasRating={hasRating}
                      isSubmitting={isLoading}
                    />
                  </div>
                </div>
              </ChatCard.Body>
            </ChatCard.Root>
          </Form>
        );
      }}
    </Formik>
  );
};
